import {
  AtxPowerAction,
  AuditLogEvent,
  FleetNode,
  KvmHardwareState,
  QualityProfile,
  SessionRecordingFrame,
  SessionRecordingManifest,
  StreamTelemetryMetrics,
  TerminalSnippet,
  VirtualMediaCatalogItem
} from '../shared/types';
import { iconSvg } from './components/Icons';
import { HardwareFrameSnapshot, KvmStreamClient } from './core-protocol-engine/KvmStreamClient';
import { DualPaneFileManager } from './file-transfer/DualPaneFileManager';
import { OnScreenKeyboard } from './kvm-hardware/OnScreenKeyboard';
import { TerminalMuxWorkspace } from './terminal-mux/TerminalMuxWorkspace';

type ServerTab = 'machines' | 'terminal' | 'sftp' | 'security';
type FloatingPopover = 'none' | 'clipboard' | 'kvm_tools';

interface EnrichedFleetNode extends FleetNode {
  hardware: KvmHardwareState;
  quality: QualityProfile;
}

class EaKvmEnterpriseApp {
  private readonly root: HTMLElement;

  // Role & Authentication State
  private appRole: 'agent' | 'server' = 'agent';
  private authenticated = false;
  private currentUser: { username: string; displayName: string; role: string } | null = null;
  private loginError = '';

  // Navigation State
  // In 'agent' role, screenViewActive is always true after login.
  // In 'server' role, screenViewActive becomes true when clicking "Accéder à l'écran" on a machine.
  private screenViewActive = false;
  private serverTab: ServerTab = 'machines';
  private activeNodeId = localStorage.getItem('EA_KVM_ACTIVE_NODE') || 'ubuntu-pc';
  private activeEngineMode: 'rdp' | 'kvm' = 'rdp';

  // Floating Menu State (Fullscreen Screen View)
  private floatingPinned = true;
  private statsVisible = true;
  private activePopover: FloatingPopover = 'none';
  private clipboardText = '';

  // Data State
  private nodes: EnrichedFleetNode[] = [];
  private fleetSearch = '';
  private enrollModalOpen = false;
  private streamClient: KvmStreamClient | null = null;
  private readonly osdKeyboard: OnScreenKeyboard;
  private latestHwSnapshot: HardwareFrameSnapshot | null = null;
  private latestTelemetry: StreamTelemetryMetrics | null = null;
  private rttHistory: number[] = [0.6, 0.7, 0.5, 0.8, 0.6, 0.7];
  private mediaCatalog: VirtualMediaCatalogItem[] = [];
  private snippets: TerminalSnippet[] = [];

  // Workspaces for Server Mode
  private readonly terminalMux: TerminalMuxWorkspace;
  private readonly sftpManager: DualPaneFileManager;

  // Security & Users State
  private auditEvents: AuditLogEvent[] = [];
  private rbacUsers: Array<{ username: string; role: string; displayName: string; createdAt: number }> = [];
  private recordings: SessionRecordingManifest[] = [];
  private activeReplay: { manifest: SessionRecordingManifest; frames: SessionRecordingFrame[] } | null = null;

  constructor(root: HTMLElement) {
    this.root = root;

    this.terminalMux = new TerminalMuxWorkspace({
      getNodes: () => this.nodes,
      getSnippets: () => this.snippets,
      onSaveSnippet: async snip => {
        const res = await this.apiFetch<{ snippets: TerminalSnippet[] }>('/api/v1/terminal/snippets', {
          method: 'POST',
          body: JSON.stringify(snip)
        });
        this.snippets = res.snippets;
      },
      onStatus: () => {}
    });

    this.sftpManager = new DualPaneFileManager({
      getNodes: () => this.nodes,
      apiFetch: <T>(url: string, options?: RequestInit) => this.apiFetch<T>(url, options),
      onStatus: () => {}
    });

    this.osdKeyboard = new OnScreenKeyboard({
      onKeysym: (keysym, pressed, label) => {
        this.streamClient?.sendKeystroke(keysym, pressed, label);
      },
      onMacro: macro => {
        this.streamClient?.sendMacro(macro);
      }
    });

    void this.bootstrap();
  }

  private getStoredToken(): string {
    return sessionStorage.getItem('EA_KVM_AUTH_TOKEN') || localStorage.getItem('EA_KVM_AUTH_TOKEN') || '';
  }

  private setStoredToken(token: string): void {
    if (token) {
      sessionStorage.setItem('EA_KVM_AUTH_TOKEN', token);
      localStorage.setItem('EA_KVM_AUTH_TOKEN', token);
    } else {
      sessionStorage.removeItem('EA_KVM_AUTH_TOKEN');
      localStorage.removeItem('EA_KVM_AUTH_TOKEN');
    }
  }

  private async apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const token = this.getStoredToken();
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.body ? { 'Content-Type': 'application/json' } : {})
    };
    const res = await fetch(url, { ...options, headers });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    }
    return (await res.json()) as T;
  }

  private async bootstrap(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const shareToken = urlParams.get('share');
    if (shareToken) {
      this.setStoredToken(shareToken);
    }

    try {
      const session = await this.apiFetch<{
        authenticated: boolean;
        appRole: 'agent' | 'server';
        localNodeId: string;
        hostname: string;
        user: { username: string; displayName: string; role: string } | null;
      }>('/api/v1/auth/session');

      // Allow URL override ?role=agent or ?role=server for testing
      const forcedRole = urlParams.get('role');
      this.appRole = forcedRole === 'server' ? 'server' : forcedRole === 'agent' ? 'agent' : session.appRole;
      this.authenticated = session.authenticated;
      this.currentUser = session.user;

      if (this.appRole === 'agent') {
        this.activeNodeId = session.localNodeId || 'ubuntu-pc';
        this.activeEngineMode = 'rdp';
        this.screenViewActive = true;
      }
    } catch {
      this.authenticated = false;
    }

    if (this.authenticated) {
      await this.loadInitialData();
    }

    this.render();

    window.setInterval(() => {
      if (this.authenticated && this.appRole === 'server' && !this.screenViewActive && this.serverTab === 'machines') {
        void this.loadFleetNodes(true);
      }
    }, 8000);
  }

  private async loadInitialData(): Promise<void> {
    await Promise.all([
      this.loadFleetNodes(true),
      this.loadKvmCatalogAndProfiles(),
      this.loadSnippets()
    ]);
  }

  private async loadFleetNodes(silent = false): Promise<void> {
    try {
      const data = await this.apiFetch<{ nodes: EnrichedFleetNode[] }>('/api/v1/fleet/nodes');
      this.nodes = data.nodes;
      if (!this.nodes.some(n => n.id === this.activeNodeId) && this.nodes.length > 0) {
        this.activeNodeId = this.nodes[0].id;
      }
      if (!silent) {
        this.render();
      }
    } catch {
      // Ignore background refresh error
    }
  }

  private async loadKvmCatalogAndProfiles(): Promise<void> {
    try {
      const data = await this.apiFetch<{
        state: KvmHardwareState;
        mediaCatalog: VirtualMediaCatalogItem[];
      }>(`/api/v1/kvm/state?nodeId=${encodeURIComponent(this.activeNodeId)}`);
      this.mediaCatalog = data.mediaCatalog;
    } catch {
      // Ignore
    }
  }

  private async loadSnippets(): Promise<void> {
    try {
      const data = await this.apiFetch<{ snippets: TerminalSnippet[] }>('/api/v1/terminal/snippets');
      this.snippets = data.snippets;
    } catch {
      // Ignore
    }
  }

  private closeStreamIfOpen(): void {
    if (this.streamClient) {
      this.streamClient.destroy();
      this.streamClient = null;
    }
  }

  private logout(): void {
    this.closeStreamIfOpen();
    this.terminalMux.disposeMountedXterms();
    this.setStoredToken('');
    this.authenticated = false;
    this.currentUser = null;
    this.loginError = '';
    this.render();
  }

  private openMachineScreen(nodeId: string, mode?: 'rdp' | 'kvm'): void {
    const node = this.nodes.find(n => n.id === nodeId);
    this.activeNodeId = nodeId;
    this.activeEngineMode = mode || (node && node.rdpPort > 0 ? 'rdp' : 'kvm');
    localStorage.setItem('EA_KVM_ACTIVE_NODE', nodeId);
    this.screenViewActive = true;
    this.activePopover = 'none';
    this.render();
  }

  // ==========================================================================
  // MASTER RENDER DISPATCHER
  // ==========================================================================
  private render(): void {
    if (!this.authenticated) {
      this.closeStreamIfOpen();
      this.renderLoginScreen();
      return;
    }

    // In Agent Mode (or when a machine screen is active in Server Mode),
    // show ONLY the Fullscreen Screen + sleek Floating Menu + Stats!
    if (this.appRole === 'agent' || this.screenViewActive) {
      this.renderFullscreenScreenView();
      return;
    }

    // Otherwise, in Remotly Server Mode, show the clean Server Dashboard
    this.renderServerDashboard();
  }

  // ==========================================================================
  // 1. AUTHENTICATION PAGE (AGENT & REMOTLY SERVER)
  // ==========================================================================
  private renderLoginScreen(): void {
    const isAgent = this.appRole === 'agent';
    const hostLabel = window.location.host;

    this.root.innerHTML = `
      <div class="nx-auth-screen">
        <form id="nx-login-form" class="nx-auth-card">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;">
            <div style="display:flex;align-items:center;gap:10px;">
              <span class="nx-brand-badge" style="width:28px;height:28px;font-size:12px;">${isAgent ? 'AG' : 'RM'}</span>
              <div>
                <div style="font-weight:600;font-size:14px;color:#F3F4F6;">
                  ${isAgent ? 'Agent Machine Direct' : 'Remotly Server'}
                </div>
                <div class="mono" style="font-size:11px;color:#9CA3AF;">${this.escapeHtml(hostLabel)}</div>
              </div>
            </div>
            <span class="nx-tag mono" style="color:#10B981;border-color:#10B981;">
              ${isAgent ? 'AGENT LOCAL' : 'ORCHESTRATOR'}
            </span>
          </div>

          <div style="font-size:12px;color:#9CA3AF;margin-bottom:18px;line-height:1.5;">
            ${isAgent
              ? 'Authentifiez-vous pour accéder directement à l écran de cette machine.'
              : 'Connectez-vous à l orchestrateur central pour superviser et contrôler vos machines.'}
          </div>

          ${this.loginError ? `
            <div style="padding:8px 10px;margin-bottom:14px;background:rgba(239,68,68,0.12);border:1px solid #EF4444;border-radius:4px;color:#EF4444;font-size:12px;">
              ${this.escapeHtml(this.loginError)}
            </div>
          ` : ''}

          <div style="display:flex;flex-direction:column;gap:12px;">
            <div>
              <label style="display:block;font-size:11px;color:#9CA3AF;margin-bottom:5px;font-weight:500;">Identifiant</label>
              <input id="nx-login-user" class="nx-input mono" style="width:100%;height:34px;padding:0 10px;" type="text" placeholder="serrakh" autocomplete="username" required autofocus />
            </div>

            <div>
              <label style="display:block;font-size:11px;color:#9CA3AF;margin-bottom:5px;font-weight:500;">Mot de passe</label>
              <input id="nx-login-pass" class="nx-input mono" style="width:100%;height:34px;padding:0 10px;" type="password" placeholder="••••••••" autocomplete="current-password" required />
            </div>

            <button class="nx-btn nx-btn-primary" style="width:100%;height:36px;margin-top:6px;font-size:13px;font-weight:600;" type="submit">
              ${iconSvg('lock', 14)}
              <span>${isAgent ? 'Accéder à la machine' : 'Se connecter à Remotly'}</span>
            </button>
          </div>
        </form>
      </div>
    `;

    const form = this.root.querySelector('#nx-login-form') as HTMLFormElement | null;
    form?.addEventListener('submit', async e => {
      e.preventDefault();
      const username = (this.root.querySelector('#nx-login-user') as HTMLInputElement).value.trim();
      const password = (this.root.querySelector('#nx-login-pass') as HTMLInputElement).value;
      try {
        this.loginError = '';
        const res = await this.apiFetch<{
          token: string;
          user: { username: string; displayName: string; role: string };
        }>('/api/v1/auth/login', {
          method: 'POST',
          body: JSON.stringify({ username, password })
        });
        this.setStoredToken(res.token);
        this.authenticated = true;
        this.currentUser = res.user;
        await this.loadInitialData();
        if (this.appRole === 'agent') {
          this.screenViewActive = true;
        }
        this.render();
      } catch {
        this.loginError = 'Identifiant ou mot de passe incorrect.';
        this.renderLoginScreen();
      }
    });
  }

  // ==========================================================================
  // 2. FULLSCREEN SCREEN + FLOATING MENU + STATS (AGENT & REMOTE SESSION)
  // ==========================================================================
  private renderFullscreenScreenView(): void {
    const node = this.nodes.find(n => n.id === this.activeNodeId) || this.nodes[0];
    const hw = this.latestHwSnapshot?.hardware || node?.hardware;
    const nodeLabel = node ? `${node.name} (${node.ip})` : window.location.hostname;

    this.root.innerHTML = `
      <div id="nx-fullscreen-stage" class="nx-agent-fullscreen">
        <!-- Top-Center Sleek Retractable Floating Menu -->
        <div id="nx-floating-zone" class="nx-floating-zone ${this.floatingPinned ? '' : 'collapsed'}">
          <div class="nx-floating-pill">
            ${this.appRole === 'server' ? `
              <button id="nx-float-back" class="nx-btn" style="height:26px;padding:0 8px;" title="Retour à la liste des machines" type="button">
                ← Machines
              </button>
              <div class="nx-floating-divider"></div>
            ` : ''}

            <div style="display:flex;align-items:center;gap:6px;padding:0 4px;">
              <span class="nx-status-dot online"></span>
              <span class="mono" style="font-size:11px;font-weight:600;color:#F3F4F6;">${this.escapeHtml(nodeLabel)}</span>
            </div>

            <div class="nx-floating-divider"></div>

            <button id="nx-float-stats" class="nx-btn ${this.statsVisible ? 'nx-btn-emerald' : ''}" style="height:26px;padding:0 8px;" type="button">
              ${iconSvg('activity', 13)}
              <span>Stats</span>
            </button>

            <button id="nx-float-clip" class="nx-btn ${this.activePopover === 'clipboard' ? 'nx-btn-primary' : ''}" style="height:26px;padding:0 8px;" type="button">
              ${iconSvg('clipboard', 13)}
              <span>Presse-papiers</span>
            </button>

            <button id="nx-float-kbd" class="nx-btn ${this.osdKeyboard.isVisible() ? 'nx-btn-primary' : ''}" style="height:26px;padding:0 8px;" title="Clavier visuel" type="button">
              ${iconSvg('keyboard', 13)}
              <span>Clavier</span>
            </button>

            <button id="nx-float-cad" class="nx-btn" style="height:26px;padding:0 8px;" title="Envoyer Ctrl+Alt+Suppr" type="button">
              <span>Ctrl+Alt+Suppr</span>
            </button>

            <button id="nx-float-kvm-tools" class="nx-btn ${this.activePopover === 'kvm_tools' ? 'nx-btn-primary' : ''}" style="height:26px;padding:0 8px;" title="Options d'affichage, Alimentation & ISO" type="button">
              ${iconSvg('sliders', 13)}
              <span>Options</span>
            </button>

            <button id="nx-float-fullscreen" class="nx-btn" style="height:26px;padding:0 8px;" title="Plein écran" type="button">
              ${iconSvg('maximize', 13)}
            </button>

            <button id="nx-float-pin" class="nx-btn ${this.floatingPinned ? 'nx-btn-primary' : ''}" style="height:26px;padding:0 7px;" title="Épingler ou masquer automatiquement le menu" type="button">
              ${iconSvg('pin', 12)}
            </button>

            <div class="nx-floating-divider"></div>

            <button id="nx-float-logout" class="nx-btn nx-btn-danger" style="height:26px;padding:0 8px;" title="Déconnexion" type="button">
              ${iconSvg('power', 12)}
              <span>${this.appRole === 'agent' ? 'Déconnexion' : 'Fermer'}</span>
            </button>
          </div>

          ${this.renderFloatingPopover(hw)}
        </div>

        <!-- Compact Real-Time Stats Overlay (Toggleable via 'Stats' button) -->
        <div id="nx-stats-overlay" class="nx-floating-stats" style="display:${this.statsVisible ? 'block' : 'none'};">
          <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #1E2430;padding-bottom:4px;margin-bottom:6px;">
            <span style="color:#10B981;font-weight:600;">STATS TEMPS RÉEL</span>
            <span id="hud-fps" style="color:#F3F4F6;font-weight:600;">60 FPS</span>
          </div>
          <div class="nx-hud-row"><span style="color:#9CA3AF;">Ping / Latence :</span><span id="hud-rtt" style="color:#10B981;">0.65 ms</span></div>
          <div class="nx-hud-row"><span style="color:#9CA3AF;">Débit :</span><span id="hud-bw">8420 kbps</span></div>
          <div class="nx-hud-row"><span style="color:#9CA3AF;">Résolution :</span><span id="hud-edid">1920x1080@60Hz</span></div>
          <div class="nx-hud-row"><span style="color:#9CA3AF;">Décodage :</span><span id="hud-decode">0.48 ms</span></div>
          <canvas id="hud-sparkline" width="218" height="26" style="margin-top:6px;background:#090B0E;border:1px solid #1E2430;border-radius:3px;"></canvas>
        </div>

        <!-- Fullscreen Remote Screen Canvas -->
        <canvas id="nx-kvm-canvas" class="nx-kvm-canvas" width="1920" height="1080" tabindex="0"></canvas>

        <!-- Optional On-Screen Virtual Keyboard -->
        ${this.osdKeyboard.renderHtml()}
      </div>
    `;

    const canvas = this.root.querySelector('#nx-kvm-canvas') as HTMLCanvasElement;
    this.closeStreamIfOpen();
    this.streamClient = new KvmStreamClient(canvas, this.activeNodeId, this.activeEngineMode);
    this.streamClient.onTelemetry((metrics, history) => {
      this.latestTelemetry = metrics;
      this.rttHistory = history;
      this.updateFloatingStatsDom();
    });
    this.streamClient.onHardwareState(snap => {
      this.latestHwSnapshot = snap;
    });

    this.bindFullscreenFloatingEvents();
  }

  private renderFloatingPopover(hw?: KvmHardwareState): string {
    if (this.activePopover === 'clipboard') {
      return `
        <div class="nx-floating-popover">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
            <span style="font-weight:600;font-size:12px;">Presse-papiers &amp; Frappe distante</span>
            <button id="nx-close-popover" class="nx-btn" style="height:22px;padding:0 6px;" type="button">${iconSvg('x', 11)}</button>
          </div>
          <textarea id="nx-float-clip-text" class="nx-input mono" style="width:100%;height:70px;padding:8px;resize:vertical;" placeholder="Collez votre texte ou mot de passe ici...">${this.escapeHtml(this.clipboardText)}</textarea>
          <div style="display:flex;gap:6px;margin-top:8px;">
            <button id="nx-btn-send-clip" class="nx-btn nx-btn-primary" style="flex:1;" type="button">
              ${iconSvg('clipboard', 12)} Synchroniser Presse-papiers
            </button>
            <button id="nx-btn-type-keys" class="nx-btn" style="flex:1;" title="Simuler la frappe clavier caractère par caractère" type="button">
              ${iconSvg('keyboard', 12)} Frapper le texte
            </button>
          </div>
        </div>
      `;
    }

    if (this.activePopover === 'kvm_tools') {
      return `
        <div class="nx-floating-popover" style="width:370px;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
            <span style="font-weight:600;font-size:12px;">Options d'Affichage &amp; Contrôle Matériel</span>
            <button id="nx-close-popover" class="nx-btn" style="height:22px;padding:0 6px;" type="button">${iconSvg('x', 11)}</button>
          </div>

          <div style="display:flex;flex-direction:column;gap:10px;">
            <div>
              <div style="font-size:11px;color:#9CA3AF;margin-bottom:4px;">Source d'affichage</div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
                <button id="nx-opt-mode-rdp" class="nx-btn ${this.activeEngineMode === 'rdp' ? 'nx-btn-primary' : ''}" type="button">
                  ${iconSvg('monitor', 12)} Bureau Direct (RDP)
                </button>
                <button id="nx-opt-mode-kvm" class="nx-btn ${this.activeEngineMode === 'kvm' ? 'nx-btn-primary' : ''}" type="button">
                  ${iconSvg('cpu', 12)} Console KVM / BIOS
                </button>
              </div>
            </div>

            <div>
              <div style="font-size:11px;color:#9CA3AF;margin-bottom:4px;">Alimentation &amp; Wake-on-LAN (${this.escapeHtml(hw?.powerState || 'S0_ON')})</div>
              <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;">
                <button class="nx-btn nx-btn-emerald act-float-atx" data-atx="WAKE_ON_LAN" type="button">${iconSvg('zap', 12)} WoL</button>
                <button class="nx-btn act-float-atx" data-atx="COLD_RESET" type="button">${iconSvg('rotate-ccw', 12)} Reset</button>
                <button class="nx-btn nx-btn-danger act-float-atx" data-atx="POWER_LONG_5S" type="button">${iconSvg('power', 12)} Power</button>
              </div>
            </div>

            <div>
              <div style="font-size:11px;color:#9CA3AF;margin-bottom:4px;">Image ISO / USB Virtuel</div>
              <div style="display:flex;gap:6px;">
                <select id="nx-float-iso-select" class="nx-select mono" style="flex:1;">
                  ${this.mediaCatalog.map(c => `<option value="${this.escapeHtml(c.urlOrPath)}" data-name="${this.escapeHtml(c.name)}">${this.escapeHtml(c.name)}</option>`).join('')}
                </select>
                <button id="nx-float-mount-iso" class="nx-btn nx-btn-primary" type="button">Monter</button>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    return '';
  }

  private bindFullscreenFloatingEvents(): void {
    const stage = this.root;

    stage.querySelector('#nx-float-back')?.addEventListener('click', () => {
      this.closeStreamIfOpen();
      this.screenViewActive = false;
      this.render();
    });

    stage.querySelector('#nx-float-stats')?.addEventListener('click', () => {
      this.statsVisible = !this.statsVisible;
      const el = document.getElementById('nx-stats-overlay');
      if (el) el.style.display = this.statsVisible ? 'block' : 'none';
      const btn = document.getElementById('nx-float-stats');
      if (btn) btn.classList.toggle('nx-btn-emerald', this.statsVisible);
    });

    stage.querySelector('#nx-float-clip')?.addEventListener('click', () => {
      this.activePopover = this.activePopover === 'clipboard' ? 'none' : 'clipboard';
      this.renderFullscreenScreenView();
    });

    stage.querySelector('#nx-float-kbd')?.addEventListener('click', () => {
      this.osdKeyboard.toggleVisible();
      this.renderFullscreenScreenView();
    });

    this.osdKeyboard.bindEvents(this.root, () => this.renderFullscreenScreenView());

    stage.querySelector('#nx-float-cad')?.addEventListener('click', () => {
      this.streamClient?.sendMacro('CTRL_ALT_DEL');
    });

    stage.querySelector('#nx-float-kvm-tools')?.addEventListener('click', () => {
      this.activePopover = this.activePopover === 'kvm_tools' ? 'none' : 'kvm_tools';
      this.renderFullscreenScreenView();
    });

    stage.querySelector('#nx-close-popover')?.addEventListener('click', () => {
      this.activePopover = 'none';
      this.renderFullscreenScreenView();
    });

    stage.querySelector('#nx-float-fullscreen')?.addEventListener('click', () => {
      const fsEl = document.getElementById('nx-fullscreen-stage');
      if (!document.fullscreenElement && fsEl?.requestFullscreen) {
        void fsEl.requestFullscreen();
      } else if (document.exitFullscreen) {
        void document.exitFullscreen();
      }
    });

    stage.querySelector('#nx-float-pin')?.addEventListener('click', () => {
      this.floatingPinned = !this.floatingPinned;
      const zone = document.getElementById('nx-floating-zone');
      if (zone) zone.classList.toggle('collapsed', !this.floatingPinned);
      const pinBtn = document.getElementById('nx-float-pin');
      if (pinBtn) pinBtn.classList.toggle('nx-btn-primary', this.floatingPinned);
    });

    stage.querySelector('#nx-float-logout')?.addEventListener('click', () => {
      if (this.appRole === 'agent') {
        this.logout();
      } else {
        this.closeStreamIfOpen();
        this.screenViewActive = false;
        this.render();
      }
    });

    // Popover: Clipboard & Type Keys
    stage.querySelector('#nx-btn-send-clip')?.addEventListener('click', async () => {
      const txt = (stage.querySelector('#nx-float-clip-text') as HTMLTextAreaElement).value;
      this.clipboardText = txt;
      await this.apiFetch('/api/v1/desktop/clipboard', {
        method: 'POST',
        body: JSON.stringify({ nodeId: this.activeNodeId, text: txt, syncEnabled: true })
      });
      this.activePopover = 'none';
      this.renderFullscreenScreenView();
    });

    stage.querySelector('#nx-btn-type-keys')?.addEventListener('click', async () => {
      const txt = (stage.querySelector('#nx-float-clip-text') as HTMLTextAreaElement).value;
      this.clipboardText = txt;
      await this.apiFetch('/api/v1/kvm/hid/paste', {
        method: 'POST',
        body: JSON.stringify({
          nodeId: this.activeNodeId,
          text: txt,
          keyboardLayout: 'AZERTY_FR',
          interKeyDelayMs: 12
        })
      });
      this.activePopover = 'none';
      this.renderFullscreenScreenView();
    });

    // Popover: Display Mode & KVM Options
    stage.querySelector('#nx-opt-mode-rdp')?.addEventListener('click', () => {
      this.activeEngineMode = 'rdp';
      this.activePopover = 'none';
      this.renderFullscreenScreenView();
    });

    stage.querySelector('#nx-opt-mode-kvm')?.addEventListener('click', () => {
      this.activeEngineMode = 'kvm';
      this.activePopover = 'none';
      this.renderFullscreenScreenView();
    });

    stage.querySelectorAll('.act-float-atx').forEach(btn => {
      btn.addEventListener('click', async () => {
        const action = ((btn as HTMLElement).dataset.atx as AtxPowerAction) || 'WAKE_ON_LAN';
        await this.executeAtxAction(this.activeNodeId, action);
        this.activePopover = 'none';
        this.renderFullscreenScreenView();
      });
    });

    stage.querySelector('#nx-float-mount-iso')?.addEventListener('click', async () => {
      const sel = stage.querySelector('#nx-float-iso-select') as HTMLSelectElement;
      const opt = sel.options[sel.selectedIndex];
      await this.apiFetch('/api/v1/kvm/media/mount', {
        method: 'POST',
        body: JSON.stringify({
          nodeId: this.activeNodeId,
          name: opt?.dataset.name,
          sourceType: 'INTERNAL_STORAGE',
          sourceUrlOrPath: sel.value,
          deviceType: 'CDROM_ISO',
          readOnly: true
        })
      });
      this.activePopover = 'none';
      this.renderFullscreenScreenView();
    });
  }

  private updateFloatingStatsDom(): void {
    if (!this.statsVisible || !this.latestTelemetry) return;
    const m = this.latestTelemetry;
    const setTxt = (id: string, val: string) => {
      const el = document.getElementById(id);
      if (el) el.textContent = val;
    };
    setTxt('hud-fps', `${m.fpsDecoded} FPS`);
    setTxt('hud-rtt', `${m.rttMs.toFixed(2)} ms`);
    setTxt('hud-bw', `${m.downlinkKbps} kbps`);
    setTxt('hud-edid', m.edidResolution);
    setTxt('hud-decode', `${m.frameDecodeMs.toFixed(2)} ms`);

    const spark = document.getElementById('hud-sparkline') as HTMLCanvasElement | null;
    if (spark) {
      const sctx = spark.getContext('2d');
      if (sctx) {
        sctx.fillStyle = '#090B0E';
        sctx.fillRect(0, 0, spark.width, spark.height);
        sctx.strokeStyle = '#10B981';
        sctx.lineWidth = 1.5;
        sctx.beginPath();
        const hist = this.rttHistory;
        const maxVal = Math.max(2.0, ...hist);
        hist.forEach((v, idx) => {
          const x = (idx / Math.max(1, hist.length - 1)) * (spark.width - 8) + 4;
          const y = spark.height - 4 - (v / maxVal) * (spark.height - 8);
          if (idx === 0) sctx.moveTo(x, y);
          else sctx.lineTo(x, y);
        });
        sctx.stroke();
      }
    }
  }

  // ==========================================================================
  // 3. REMOTLY SERVER DASHBOARD (CLEAN, ERGONOMIC MULTI-MACHINE ORCHESTRATOR)
  // ==========================================================================
  private renderServerDashboard(): void {
    this.root.innerHTML = `
      <header class="nx-topbar" style="height:48px;padding:0 18px;">
        <div class="nx-brand">
          <span class="nx-brand-badge">RM</span>
          <span style="font-size:14px;">REMOTLY ORCHESTRATOR</span>
          <span class="nx-tag mono" style="color:#10B981;border-color:#10B981;">SERVER</span>
        </div>

        <nav class="nx-nav-tabs" style="gap:8px;">
          <button class="nx-nav-tab ${this.serverTab === 'machines' ? 'active' : ''}" data-tab="machines">
            ${iconSvg('monitor', 14)}
            <span>Mes Machines (${this.nodes.length})</span>
          </button>
          <button class="nx-nav-tab ${this.serverTab === 'terminal' ? 'active' : ''}" data-tab="terminal">
            ${iconSvg('terminal', 14)}
            <span>Terminal SSH</span>
          </button>
          <button class="nx-nav-tab ${this.serverTab === 'sftp' ? 'active' : ''}" data-tab="sftp">
            ${iconSvg('folder', 14)}
            <span>Fichiers SFTP</span>
          </button>
          <button class="nx-nav-tab ${this.serverTab === 'security' ? 'active' : ''}" data-tab="security">
            ${iconSvg('shield-check', 14)}
            <span>Utilisateurs &amp; Journal</span>
          </button>
        </nav>

        <div class="nx-topbar-right">
          <span class="mono" style="font-size:11px;color:#9CA3AF;">${this.escapeHtml(this.currentUser?.username || 'admin')}</span>
          <button id="nx-srv-logout" class="nx-btn nx-btn-danger" type="button">
            ${iconSvg('power', 12)}
            <span>Déconnexion</span>
          </button>
        </div>
      </header>

      <main id="nx-workspace-body" class="nx-workspace"></main>
    `;

    this.root.querySelectorAll('.nx-nav-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.serverTab === 'terminal') {
          this.terminalMux.disposeMountedXterms();
        }
        this.serverTab = ((btn as HTMLElement).dataset.tab as ServerTab) || 'machines';
        this.renderServerDashboard();
      });
    });

    this.root.querySelector('#nx-srv-logout')?.addEventListener('click', () => {
      this.logout();
    });

    const body = document.getElementById('nx-workspace-body');
    if (!body) return;

    if (this.serverTab === 'machines') {
      this.renderMachinesGrid(body);
    } else if (this.serverTab === 'terminal') {
      this.terminalMux.render(body);
    } else if (this.serverTab === 'sftp') {
      this.sftpManager.render(body);
      void this.sftpManager.refreshBothPanes(body);
    } else if (this.serverTab === 'security') {
      void this.loadSecurityTab(body);
    }
  }

  private renderMachinesGrid(container: HTMLElement): void {
    const filtered = this.nodes.filter(n => {
      if (!this.fleetSearch.trim()) return true;
      const q = this.fleetSearch.toLowerCase();
      return (
        n.name.toLowerCase().includes(q) ||
        n.ip.toLowerCase().includes(q) ||
        n.osVersion.toLowerCase().includes(q)
      );
    });

    container.innerHTML = `
      <div class="nx-fleet-container">
        <div class="nx-filter-bar" style="padding:12px 18px;">
          <div class="nx-filter-group">
            <input id="nx-srv-search" class="nx-input" style="width:280px;height:32px;" type="text" placeholder="Rechercher une machine (Nom, IP)..." value="${this.escapeHtml(this.fleetSearch)}" />
          </div>
          <div class="nx-filter-group">
            <button id="nx-btn-enroll" class="nx-btn nx-btn-primary" style="height:32px;" type="button">
              ${iconSvg('plus', 13)} Ajouter une machine
            </button>
            <a class="nx-btn nx-btn-emerald" style="height:32px;" href="/api/v1/fleet/agent-download?os=macos" download="EA-KVM-Agent.dmg">
              ${iconSvg('download', 13)} Agent macOS (.DMG)
            </a>
            <a class="nx-btn" style="height:32px;" href="/api/v1/fleet/agent-download?os=linux" download="enroll-ea-kvm-agent.sh">
              ${iconSvg('download', 13)} Agent Linux (.SH)
            </a>
          </div>
        </div>

        <div class="nx-fleet-scroll" style="padding:18px;">
          <div class="nx-fleet-grid" style="grid-template-columns:repeat(auto-fill, minmax(320px, 1fr));gap:16px;">
            ${filtered.map(n => {
              const ramPct = Math.round((n.ramUsedMb / Math.max(1, n.ramTotalMb)) * 100);
              return `
                <div class="nx-node-card" style="border-radius:6px;">
                  <div class="nx-node-card-header" style="padding:10px 12px;">
                    <div style="display:flex;align-items:center;gap:8px;">
                      <span class="nx-status-dot ${n.online ? 'online' : 'offline'}"></span>
                      <span style="font-weight:600;font-size:13px;color:#F3F4F6;">${this.escapeHtml(n.name)}</span>
                    </div>
                    <span class="mono" style="font-size:11px;color:#9CA3AF;">${this.escapeHtml(n.ip)}</span>
                  </div>

                  <div class="nx-thumb-wrap act-connect-screen" data-node="${this.escapeHtml(n.id)}" style="height:165px;cursor:pointer;" title="Cliquer pour ouvrir l'écran">
                    <img src="/api/v1/fleet/thumbnail/${encodeURIComponent(n.id)}?t=${Math.floor(Date.now() / 8000)}" alt="${this.escapeHtml(n.name)}" loading="lazy" />
                  </div>

                  <div style="display:flex;justify-content:space-between;padding:8px 12px;border-bottom:1px solid #1E2430;font-family:var(--font-mono);font-size:11px;color:#9CA3AF;">
                    <span>CPU: <strong style="color:#F3F4F6;">${n.cpuPercent.toFixed(0)}%</strong></span>
                    <span>RAM: <strong style="color:#F3F4F6;">${ramPct}%</strong></span>
                    <span>Ping: <strong style="color:#10B981;">${n.latencyMs} ms</strong></span>
                  </div>

                  <div style="padding:10px 12px;display:flex;flex-direction:column;gap:8px;">
                    <button class="nx-btn nx-btn-primary act-connect-screen" data-node="${this.escapeHtml(n.id)}" style="width:100%;height:34px;font-weight:600;" type="button">
                      ${iconSvg('monitor', 14)}
                      <span>Accéder à l'écran</span>
                    </button>

                    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;">
                      <button class="nx-btn act-open-term" data-node="${this.escapeHtml(n.id)}" type="button">
                        ${iconSvg('terminal', 12)} Terminal
                      </button>
                      <button class="nx-btn act-open-sftp" data-node="${this.escapeHtml(n.id)}" type="button">
                        ${iconSvg('folder', 12)} Fichiers
                      </button>
                      <button class="nx-btn nx-btn-emerald act-wol" data-node="${this.escapeHtml(n.id)}" title="Wake-on-LAN" type="button">
                        ${iconSvg('zap', 12)} Wake
                      </button>
                    </div>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      </div>
      ${this.enrollModalOpen ? this.renderEnrollModal() : ''}
    `;

    const sInput = container.querySelector('#nx-srv-search') as HTMLInputElement | null;
    sInput?.addEventListener('input', () => {
      this.fleetSearch = sInput.value;
      this.renderMachinesGrid(container);
    });

    container.querySelectorAll('.act-connect-screen').forEach(el => {
      el.addEventListener('click', () => {
        const nodeId = (el as HTMLElement).dataset.node || 'ubuntu-pc';
        this.openMachineScreen(nodeId);
      });
    });

    container.querySelectorAll('.act-open-term').forEach(btn => {
      btn.addEventListener('click', () => {
        const nodeId = (btn as HTMLElement).dataset.node || 'ubuntu-pc';
        this.activeNodeId = nodeId;
        this.serverTab = 'terminal';
        this.renderServerDashboard();
        const body = document.getElementById('nx-workspace-body');
        this.terminalMux.spawnPaneForNode(nodeId, body || undefined);
      });
    });

    container.querySelectorAll('.act-open-sftp').forEach(btn => {
      btn.addEventListener('click', () => {
        const nodeId = (btn as HTMLElement).dataset.node || 'ubuntu-pc';
        this.sftpManager.setRightTargetNode(nodeId);
        this.serverTab = 'sftp';
        this.renderServerDashboard();
      });
    });

    container.querySelectorAll('.act-wol').forEach(btn => {
      btn.addEventListener('click', () => {
        const nodeId = (btn as HTMLElement).dataset.node || 'ubuntu-pc';
        void this.executeAtxAction(nodeId, 'WAKE_ON_LAN');
      });
    });

    container.querySelector('#nx-btn-enroll')?.addEventListener('click', () => {
      this.enrollModalOpen = true;
      this.renderMachinesGrid(container);
    });

    container.querySelector('#nx-close-enroll-modal')?.addEventListener('click', () => {
      this.enrollModalOpen = false;
      this.renderMachinesGrid(container);
    });

    container.querySelector('#nx-submit-enroll-node')?.addEventListener('click', async () => {
      const name = (container.querySelector('#nx-enr-name') as HTMLInputElement).value.trim() || 'Nouvelle Machine';
      const ip = (container.querySelector('#nx-enr-ip') as HTMLInputElement).value.trim() || '192.168.88.200';
      const mac = (container.querySelector('#nx-enr-mac') as HTMLInputElement).value.trim() || '02:EA:4B:56:99:01';
      const os = (container.querySelector('#nx-enr-os') as HTMLSelectElement).value;
      const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      await this.apiFetch('/api/v1/fleet/nodes', {
        method: 'POST',
        body: JSON.stringify({
          id,
          name,
          hostname: id,
          ip,
          mac,
          group: 'Machines',
          os,
          osVersion: os.toUpperCase(),
          tags: [os]
        })
      });
      this.enrollModalOpen = false;
      await this.loadFleetNodes(false);
    });
  }

  private renderEnrollModal(): string {
    return `
      <div class="nx-modal-backdrop">
        <div class="nx-cmd-modal" style="width:440px;padding:16px;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">
            <span style="font-weight:600;font-size:13px;">Ajouter une machine</span>
            <button id="nx-close-enroll-modal" class="nx-btn" type="button">${iconSvg('x', 13)}</button>
          </div>
          <div style="display:flex;flex-direction:column;gap:10px;">
            <input id="nx-enr-name" class="nx-input" style="width:100%;height:32px;" type="text" placeholder="Nom de la machine (ex: MacBook Pro)" />
            <input id="nx-enr-ip" class="nx-input mono" style="width:100%;height:32px;" type="text" placeholder="Adresse IP (ex: 192.168.88.140)" />
            <input id="nx-enr-mac" class="nx-input mono" style="width:100%;height:32px;" type="text" placeholder="Adresse MAC (optionnel, pour Wake-on-LAN)" />
            <select id="nx-enr-os" class="nx-select" style="width:100%;height:32px;">
              <option value="linux">Linux (Ubuntu / Debian)</option>
              <option value="macos">macOS</option>
              <option value="proxmox">Proxmox VE</option>
            </select>
            <button id="nx-submit-enroll-node" class="nx-btn nx-btn-primary" style="width:100%;height:34px;margin-top:4px;" type="button">
              ${iconSvg('check', 13)} Enregistrer
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private async loadSecurityTab(container: HTMLElement): Promise<void> {
    const [aud, usr, recs] = await Promise.all([
      this.apiFetch<{ events: AuditLogEvent[]; integrityValid: boolean }>('/api/v1/audit/events?limit=80'),
      this.apiFetch<{ users: Array<{ username: string; role: string; displayName: string; createdAt: number }> }>('/api/v1/auth/users'),
      this.apiFetch<{ recordings: SessionRecordingManifest[] }>('/api/v1/recordings')
    ]);
    this.auditEvents = aud.events;
    this.rbacUsers = usr.users;
    this.recordings = recs.recordings;
    if (!this.activeReplay && this.recordings.length > 0) {
      this.activeReplay = await this.apiFetch<{ manifest: SessionRecordingManifest; frames: SessionRecordingFrame[] }>(
        `/api/v1/recordings/${encodeURIComponent(this.recordings[0].id)}`
      );
    }

    container.innerHTML = `
      <div style="flex:1;display:grid;grid-template-columns:380px 1fr;gap:14px;padding:16px;overflow:hidden;">
        <!-- Comptes Utilisateurs -->
        <div style="display:flex;flex-direction:column;background:#101318;border:1px solid #1E2430;border-radius:6px;overflow:hidden;">
          <div style="padding:10px 14px;background:#161A22;border-bottom:1px solid #1E2430;font-weight:600;">
            Comptes Utilisateurs (${this.rbacUsers.length})
          </div>
          <div style="padding:12px;border-bottom:1px solid #1E2430;display:flex;flex-direction:column;gap:8px;">
            <input id="nx-rbac-user" class="nx-input mono" style="width:100%;" type="text" placeholder="Identifiant" />
            <input id="nx-rbac-pass" class="nx-input mono" style="width:100%;" type="password" placeholder="Mot de passe" />
            <div style="display:flex;gap:6px;">
              <select id="nx-rbac-role" class="nx-select" style="flex:1;">
                <option value="admin">Administrateur</option>
                <option value="operator" selected>Opérateur</option>
              </select>
              <button id="nx-btn-add-user" class="nx-btn nx-btn-primary" type="button">${iconSvg('plus', 12)} Ajouter</button>
            </div>
          </div>
          <div style="flex:1;overflow-y:auto;">
            <table class="nx-table">
              <thead><tr><th>Utilisateur</th><th>Rôle</th><th>Action</th></tr></thead>
              <tbody>
                ${this.rbacUsers.map(u => `
                  <tr>
                    <td class="mono" style="font-weight:600;">${this.escapeHtml(u.username)}</td>
                    <td><span class="nx-tag mono">${this.escapeHtml(u.role)}</span></td>
                    <td>
                      ${u.username !== 'serrakh' ? `<button class="nx-btn nx-btn-danger act-del-user" data-user="${this.escapeHtml(u.username)}" style="height:22px;padding:0 6px;font-size:10px;" type="button">Suppr</button>` : '-'}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>

        <!-- Journal d'Audit -->
        <div style="display:flex;flex-direction:column;background:#101318;border:1px solid #1E2430;border-radius:6px;overflow:hidden;">
          <div style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:#161A22;border-bottom:1px solid #1E2430;">
            <span style="font-weight:600;">Journal de Connexions &amp; Sécurité</span>
            <span class="nx-tag mono" style="color:#10B981;border-color:#10B981;">SHA-256 VÉRIFIÉ</span>
          </div>
          <div style="flex:1;overflow-y:auto;">
            <table class="nx-table">
              <thead>
                <tr><th>Heure</th><th>Utilisateur</th><th>Machine</th><th>Action</th><th>Détails</th></tr>
              </thead>
              <tbody>
                ${this.auditEvents.map(ev => `
                  <tr>
                    <td class="mono" style="color:#9CA3AF;">${new Date(ev.timestamp).toISOString().slice(11, 19)}</td>
                    <td class="mono">${this.escapeHtml(ev.actor)}</td>
                    <td class="mono">${this.escapeHtml(ev.nodeId)}</td>
                    <td><span class="nx-tag mono">${this.escapeHtml(ev.action)}</span></td>
                    <td style="color:#9CA3AF;">${this.escapeHtml(ev.details.slice(0, 70))}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;

    container.querySelector('#nx-btn-add-user')?.addEventListener('click', async () => {
      const username = (container.querySelector('#nx-rbac-user') as HTMLInputElement).value.trim();
      const password = (container.querySelector('#nx-rbac-pass') as HTMLInputElement).value;
      const role = (container.querySelector('#nx-rbac-role') as HTMLSelectElement).value;
      if (!username) return;
      await this.apiFetch('/api/v1/auth/users', {
        method: 'POST',
        body: JSON.stringify({ username, displayName: username, password, role })
      });
      await this.loadSecurityTab(container);
    });

    container.querySelectorAll('.act-del-user').forEach(btn => {
      btn.addEventListener('click', async () => {
        const u = (btn as HTMLElement).dataset.user || '';
        await this.apiFetch(`/api/v1/auth/users?username=${encodeURIComponent(u)}`, { method: 'DELETE' });
        await this.loadSecurityTab(container);
      });
    });
  }

  private async executeAtxAction(nodeId: string, action: AtxPowerAction): Promise<void> {
    await this.apiFetch('/api/v1/kvm/atx', {
      method: 'POST',
      body: JSON.stringify({ nodeId, action })
    });
    await this.loadFleetNodes(true);
  }

  private escapeHtml(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const root = document.getElementById('nx-root');
  if (root) {
    new EaKvmEnterpriseApp(root);
  }
});
